import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import api from "@/lib/api";

const ITEMS = [
  { key: "retinoid", label: "Retinoid", default_cadence: "alternate_then_daily",
    cadence_help: "Standard ramp-up: every other evening for 2 weeks, then daily as tolerated." },
  { key: "vitamin_c", label: "Vitamin C", default_cadence: "daily",
    cadence_help: "Once your readiness check is passed, vitamin C is daily in AM." },
  { key: "exfoliating_cleanser", label: "Exfoliating Cleanser", default_cadence: "twice_weekly_step_up",
    cadence_help: "Start no more than 2 nights/week; +1 night every 2 weeks as tolerated." },
];

const CADENCES = [
  { key: "alternate_then_daily", label: "Every-other-day for 2 weeks → daily" },
  { key: "daily", label: "Daily" },
  { key: "twice_weekly_step_up", label: "Twice weekly → step up" },
];

const TINTS = {
  retinoid: "aa-card-stone",
  vitamin_c: "aa-card-warm",
  exfoliating_cleanser: "aa-card-sage",
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function Tracker() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState({});

  const load = async () => {
    const r = await api.get("/tracker");
    setData(r.data);
  };

  useEffect(() => { load(); }, []);

  const startItem = async (itemKey, form) => {
    setSaving((s) => ({ ...s, [itemKey]: true }));
    try {
      const r = await api.put("/tracker/start", {
        item: itemKey,
        product_name: form.product_name || "",
        start_date: form.start_date,
        cadence: form.cadence,
      });
      setData(r.data);
    } finally { setSaving((s) => ({ ...s, [itemKey]: false })); }
  };

  const logToday = async (itemKey) => {
    setSaving((s) => ({ ...s, [itemKey]: true }));
    try {
      const r = await api.post("/tracker/log", { item: itemKey, date: todayIso() });
      setData(r.data);
    } finally { setSaving((s) => ({ ...s, [itemKey]: false })); }
  };

  const unlog = async (itemKey, date) => {
    const r = await api.delete(`/tracker/log/${itemKey}/${date}`);
    setData(r.data);
  };

  const stopItem = async (itemKey) => {
    if (!window.confirm(`Stop tracking ${ITEMS.find(i => i.key === itemKey).label}? Your logs will be cleared.`)) return;
    const r = await api.delete(`/tracker/${itemKey}`);
    setData(r.data);
  };

  const toggleSuggestions = async (val) => {
    const r = await api.put("/tracker/settings", { suggestions_enabled: val });
    setData(r.data);
  };

  if (!data) return <><NavBar /><div className="max-w-5xl mx-auto px-6 py-16 aa-eyebrow">Loading…</div></>;

  return (
    <div className="App" data-testid="page-tracker">
      <NavBar />
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
        <div className="aa-eyebrow mb-3">Application tracker</div>
        <h1 className="font-display text-4xl lg:text-5xl leading-tight">Stay on <span className="serif-italic">cadence</span>.</h1>
        <p className="mt-4 text-[var(--aa-text-2)] max-w-2xl">Track when you started ramping up a new active and log each application. We'll keep the schedule straight so you don't have to.</p>

        <div className="mt-8 flex items-center gap-3" data-testid="tracker-suggestions-toggle">
          <span className="aa-eyebrow">Suggestions:</span>
          <button
            data-testid="tracker-suggestions-on"
            onClick={() => toggleSuggestions(true)}
            className={`aa-pill px-4 py-2 text-sm ${data.suggestions_enabled ? "bg-[var(--aa-text)] text-[var(--aa-bg)] border-[var(--aa-text)]" : "hover:bg-[var(--aa-elevated)]"}`}
          >On</button>
          <button
            data-testid="tracker-suggestions-off"
            onClick={() => toggleSuggestions(false)}
            className={`aa-pill px-4 py-2 text-sm ${!data.suggestions_enabled ? "bg-[var(--aa-text)] text-[var(--aa-bg)] border-[var(--aa-text)]" : "hover:bg-[var(--aa-elevated)]"}`}
          >Off</button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          {ITEMS.map((it) => {
            const tracked = data.items[it.key];
            return (
              <div key={it.key} className={`aa-card ${TINTS[it.key]} p-6`} data-testid={`tracker-card-${it.key}`}>
                <div className="aa-eyebrow mb-1">{it.label}</div>
                {!tracked ? (
                  <StartForm
                    item={it}
                    onStart={(form) => startItem(it.key, form)}
                    saving={!!saving[it.key]}
                  />
                ) : (
                  <TrackedItem
                    item={it}
                    tracked={tracked}
                    suggestionsEnabled={data.suggestions_enabled}
                    onLogToday={() => logToday(it.key)}
                    onUnlog={(d) => unlog(it.key, d)}
                    onStop={() => stopItem(it.key)}
                    saving={!!saving[it.key]}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10">
          <Link to="/dashboard" className="aa-cta-secondary" data-testid="tracker-back">← Back to dashboard</Link>
        </div>
      </section>
    </div>
  );
}

function StartForm({ item, onStart, saving }) {
  const [productName, setProductName] = useState("");
  const [startDate, setStartDate] = useState(todayIso());
  const [cadence, setCadence] = useState(item.default_cadence);
  return (
    <div className="mt-2 space-y-4">
      <p className="text-sm text-[var(--aa-text-2)]">{item.cadence_help}</p>
      <div>
        <label className="aa-eyebrow block mb-1">Product (optional)</label>
        <input
          data-testid={`tracker-start-product-${item.key}`}
          value={productName}
          onChange={(e) => setProductName(e.target.value)}
          placeholder="e.g. Avène RetrinAL 0.05%"
          className="w-full bg-[var(--aa-card)] border border-[var(--aa-elevated)] rounded-md p-2 text-sm focus:outline-none focus:border-[var(--aa-text)]"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="aa-eyebrow block mb-1">Start date</label>
          <input
            data-testid={`tracker-start-date-${item.key}`}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full bg-[var(--aa-card)] border border-[var(--aa-elevated)] rounded-md p-2 text-sm focus:outline-none focus:border-[var(--aa-text)]"
          />
        </div>
        <div>
          <label className="aa-eyebrow block mb-1">Cadence</label>
          <select
            data-testid={`tracker-start-cadence-${item.key}`}
            value={cadence}
            onChange={(e) => setCadence(e.target.value)}
            className="w-full bg-[var(--aa-card)] border border-[var(--aa-elevated)] rounded-md p-2 text-sm focus:outline-none focus:border-[var(--aa-text)]"
          >
            {CADENCES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
      </div>
      <button
        data-testid={`tracker-start-btn-${item.key}`}
        onClick={() => onStart({ product_name: productName, start_date: startDate, cadence })}
        disabled={saving}
        className="aa-cta-primary"
      >
        {saving ? "Starting…" : "Start tracking →"}
      </button>
    </div>
  );
}

function TrackedItem({ item, tracked, suggestionsEnabled, onLogToday, onUnlog, onStop, saving }) {
  const logs = tracked.logs || [];
  const today = todayIso();
  const loggedToday = logs.includes(today);
  const sugg = tracked.suggestion || {};
  return (
    <div className="mt-2">
      {tracked.product_name && (
        <div className="font-display text-lg mb-1">{tracked.product_name}</div>
      )}
      <div className="text-sm text-[var(--aa-text-2)]">
        Started <strong>{tracked.start_date}</strong> · {logs.length} application{logs.length === 1 ? "" : "s"} logged
      </div>

      {suggestionsEnabled && sugg.cadence_phase && (
        <div className="mt-4 aa-card p-4 bg-[var(--aa-card)]">
          <div className="aa-eyebrow mb-1">Suggestion</div>
          <div className="text-sm text-[var(--aa-text)]">
            {sugg.is_due_today ? <strong>Apply today.</strong> : <>Next application: <strong>{sugg.next_due}</strong></>}
          </div>
          <div className="text-xs text-[var(--aa-text-3)] mt-1">{sugg.cadence_phase}</div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          data-testid={`tracker-log-${item.key}`}
          onClick={onLogToday}
          disabled={saving || loggedToday}
          className="aa-cta-primary disabled:opacity-50"
        >
          {loggedToday ? "Logged today ✓" : saving ? "Logging…" : `Log ${item.label.toLowerCase()} today`}
        </button>
        <button
          data-testid={`tracker-stop-${item.key}`}
          onClick={onStop}
          className="text-xs text-[var(--aa-text-3)] underline underline-offset-4 hover:text-[var(--aa-text)]"
        >
          Stop tracking
        </button>
      </div>

      {logs.length > 0 && (
        <MiniCalendar
          startDate={tracked.start_date}
          logs={logs}
          onUnlog={onUnlog}
          testid={`tracker-cal-${item.key}`}
        />
      )}
    </div>
  );
}

// Show last ~30 days as a simple grid of dots with the log dates highlighted.
function MiniCalendar({ startDate, logs, onUnlog, testid }) {
  const today = new Date();
  const days = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  return (
    <div className="mt-5" data-testid={testid}>
      <div className="aa-eyebrow mb-2">Last 30 days</div>
      <div className="grid grid-cols-10 gap-1.5">
        {days.map((d) => {
          const isLogged = logs.includes(d);
          const isToday = d === todayIso();
          const isBeforeStart = d < startDate;
          return (
            <button
              key={d}
              title={d}
              disabled={!isLogged}
              onClick={() => isLogged && onUnlog(d)}
              className={`h-6 w-6 rounded-full text-[10px] flex items-center justify-center border ${
                isLogged
                  ? "bg-[var(--aa-sage)] border-[var(--aa-sage)] text-white hover:opacity-80 cursor-pointer"
                  : isToday
                    ? "border-[var(--aa-text)]"
                    : isBeforeStart
                      ? "border-[var(--aa-elevated)] opacity-30"
                      : "border-[var(--aa-elevated)]"
              }`}
            >
              {parseInt(d.slice(-2), 10)}
            </button>
          );
        })}
      </div>
      <div className="text-xs text-[var(--aa-text-3)] mt-2">Tap a green dot to remove a log.</div>
    </div>
  );
}
